import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, patch, post, postForm, put } from "@/services/apiClient";

export type BranchStatus = "ACTIVE" | "INACTIVE";

export interface BranchInventory {
  id: string;
  roomTypeId: string;
  totalRooms: number;
  isBookable: boolean;
  roomType?: { id: string; name: string };
}

export interface BusinessBranch {
  id: string;
  businessId: string;
  name?: string | null;
  isPrimary: boolean;
  address: string;
  city: string;
  district?: string | null;
  state?: string | null;
  pincode?: string | null;
  lat?: number | null;
  lng?: number | null;
  phone?: string | null;
  email?: string | null;
  whatsapp?: string | null;
  operatingHours?: Record<string, unknown> | null;
  status: BranchStatus;
  isVisibleInCustomerApp: boolean;
  roomInventories: BranchInventory[];
  photos?: { id: string; url: string; isCover: boolean }[];
  _count?: { bookings: number };
}

export interface BranchPayload {
  name?: string;
  address: string;
  city: string;
  district?: string;
  state?: string;
  pincode?: string;
  lat?: number;
  lng?: number;
  phone?: string;
  email?: string;
  whatsapp?: string;
  isVisibleInCustomerApp?: boolean;
  photoUrls?: string[];
}

export interface InventoryRoom {
  id: string;
  name: string;
  branchInventories: BranchInventory[];
}

const keys = {
  list: (businessId: string) => ["branches", businessId] as const,
  inventory: (businessId: string, branchId: string) =>
    ["branches", businessId, branchId, "inventory"] as const,
};

const endpoint = (businessId: string) => `/businesses/${businessId}/branches`;

export const useBranches = (businessId: string) =>
  useQuery<BusinessBranch[]>({
    queryKey: keys.list(businessId),
    queryFn: () => get<BusinessBranch[]>(endpoint(businessId)),
    enabled: !!businessId,
  });

export const useCreateBranch = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: BranchPayload) =>
      post<BusinessBranch>(endpoint(businessId), data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.list(businessId) }),
  });
};

export const useUpdateBranch = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      branchId,
      data,
    }: {
      branchId: string;
      data: BranchPayload;
    }) => patch<BusinessBranch>(`${endpoint(businessId)}/${branchId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.list(businessId) }),
  });
};

export const useUploadBranchPhotos = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ branchId, files }: { branchId: string; files: File[] }) => {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      return postForm(`${endpoint(businessId)}/${branchId}/photos`, form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.list(businessId) }),
  });
};

export const useSetBranchStatus = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      branchId,
      status,
    }: {
      branchId: string;
      status: BranchStatus;
    }) =>
      patch<BusinessBranch>(`${endpoint(businessId)}/${branchId}/status`, {
        status,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.list(businessId) }),
  });
};

export const useBranchInventory = (businessId: string, branchId?: string) =>
  useQuery<InventoryRoom[]>({
    queryKey: keys.inventory(businessId, branchId ?? ""),
    queryFn: () =>
      get<InventoryRoom[]>(
        `${endpoint(businessId)}/${branchId}/room-inventory`,
      ),
    enabled: !!businessId && !!branchId,
  });

export const useSaveBranchInventory = (
  businessId: string,
  branchId?: string,
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      inventories: Array<{
        roomTypeId: string;
        totalRooms: number;
        isBookable: boolean;
      }>,
    ) =>
      put(`${endpoint(businessId)}/${branchId}/room-inventory`, {
        inventories,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.list(businessId) });
      qc.invalidateQueries({
        queryKey: keys.inventory(businessId, branchId ?? ""),
      });
    },
  });
};
